// Overview — the executive read on fatigue exposure across the workforce.

import React from 'react';
import { Typography } from '@mui/material';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ComparisonColumns } from '../../../../../shared/components';
import type { UseComparisonModeResult } from '../../../../../shared/hooks/useComparisonMode';
import type { ComparisonGroup } from '../../../../../shared/hooks/useComparisonGroups';
import type { DerivedMetricKey, FatigueFilters } from '../data/types';
import { useFatigueComparisonSummary, useFatigueSummary } from '../data/useFatigueData';
import { KpiCard } from '../components/KpiCard';
import { MetricBreakdownPanel } from '../components/MetricBreakdownPanel';
import { FatigueTrendChart } from '../components/charts';
import { LoadingBlock, NoteLine, RiskAlert, SectionHeading, StatRow } from '../components/primitives';

interface Props {
  filters: FatigueFilters;
  comparison: UseComparisonModeResult;
  groups: ComparisonGroup[];
  overflowCount: number;
}

/**
 * Switches between the average-mode read (its own component, so it's free to
 * call `useFatigueSummary` and other hooks without a conditional-hooks
 * violation) and the compare-mode read. Each branch is a separate component
 * instance, which is the React-sanctioned way to vary which hooks run with
 * mode — not a conditional hook call inside one component.
 */
export const OverviewSection: React.FC<Props> = ({ filters, comparison, groups, overflowCount }) =>
  comparison.enabled && groups.length > 1 ? (
    <CompareOverview groups={groups} overflowCount={overflowCount} />
  ) : (
    <AverageOverview filters={filters} />
  );

const AverageOverview: React.FC<{ filters: FatigueFilters }> = ({ filters }) => {
  const [openMetric, setOpenMetric] = React.useState<DerivedMetricKey | null>(null);
  const { data: overview, isPending, error } = useFatigueSummary(filters);

  if (isPending) {
    return (
      <div className='flex flex-col gap-4'>
        {/* FM-13 — the grid is fluid rather than a fixed 4-up, so the cards
            reflow instead of squashing on narrower screens. */}
        <div className='grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4'>
          {[0, 1, 2, 3].map((i) => (
            <LoadingBlock key={i} className='h-72' />
          ))}
        </div>
        <LoadingBlock className='h-80' />
      </div>
    );
  }

  if (error || !overview) {
    return (
      <Card>
        <CardContent className='p-6'>
          <p className='text-sm text-muted-foreground'>
            Fatigue figures could not be loaded. Check the client selection and try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  const openMetricData = overview.metrics.find((m) => m.key === openMetric);

  // Worst team by fatigue exposure, ignoring the ones with nothing to show.
  const worst = [...overview.visibleCohorts]
    .filter((row) => !row.suppressed && !row.noData)
    .sort((a, b) => b.fedPer1000 - a.fedPer1000)[0];

  if (overview.empty) {
    return (
      <Card>
        <CardHeader>
          <SectionHeading overline='Overview' title='No usable wearable data in this window' />
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          <p className='text-sm text-muted-foreground'>
            Nothing measurable came back for {overview.window.start} to {overview.window.end}. Either no participants in
            scope have connected a device, or none of them wore one during the window.
          </p>
          <StatRow
            label='People wearing'
            value={`${overview.coverage.activeUsers} of ${overview.coverage.enrolledUsers}`}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4'>
        {overview.metrics.map((metric) => (
          <KpiCard key={metric.key} metric={metric} onOpenBreakdown={() => setOpenMetric(metric.key)} />
        ))}
      </div>

      {worst && (
        <RiskAlert
          tier={worst.riskTier}
          title={`${worst.cohort.name} carries the highest fatigue exposure`}
          body={`${worst.fedPer1000} fatigue exposure days per 1,000 workdays and ${worst.lrmPct}% of shifts with load above readiness, across ${worst.cohort.headcount} people at ${worst.cohort.site}. Review the rota and rest windows for this team first.`}
        />
      )}

      <div className='grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'>
        <Card>
          <CardHeader>
            <SectionHeading
              title='Indicator trend'
              subtitle='Last 12 weeks in fortnights, all four derived indicators.'
            />
          </CardHeader>
          <CardContent className='flex flex-col gap-3'>
            <FatigueTrendChart points={overview.trend.filter((point) => !point.empty)} />
            {overview.trend.some((point) => point.empty) && (
              <NoteLine>Fortnights with no usable data are left out of the line rather than drawn as zero.</NoteLine>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Data behind these figures</CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col gap-3'>
            <StatRow
              label='People wearing'
              value={`${overview.coverage.activeUsers} of ${overview.coverage.enrolledUsers}`}
            />
            <StatRow label='Coverage' value={`${overview.coverage.coveragePct}%`} />
            <StatRow label='Confidence' value={`${overview.coverage.confidenceScore}%`} />
            <StatRow label='Valid days' value={String(overview.coverage.validDataDays)} />
            <StatRow label='Missing days' value={String(overview.coverage.missingDataDays)} />
            <NoteLine>
              Figures are cohort-level, over {overview.window.start} to {overview.window.end}. Turn on "Reliable data
              only" in the filter above to see them using only participants with reliable wear compliance.
            </NoteLine>
          </CardContent>
        </Card>
      </div>

      <MetricBreakdownPanel metricKey={openMetric} metric={openMetricData} onClose={() => setOpenMetric(null)} />
    </div>
  );
};

/**
 * Compare mode (PEAK-263): one summary fetch per selected group, rendered as
 * side-by-side columns of the same KPI cards + trend chart Average mode
 * shows for the whole cohort — mirrors how other dashboards render Compare.
 */
const CompareOverview: React.FC<{ groups: ComparisonGroup[]; overflowCount: number }> = ({ groups, overflowCount }) => {
  const results = useFatigueComparisonSummary(groups);

  return (
    <ComparisonColumns groups={groups} overflowCount={overflowCount}>
      {(group, index) => {
        const result = results[index];
        if (result.isLoading) return <LoadingBlock className='h-72' />;
        if (result.isError || !result.data) {
          return (
            <Typography variant='body2' color='text.secondary'>
              Figures could not be loaded for {group.label}.
            </Typography>
          );
        }
        const overview = result.data;
        return (
          <div className='flex flex-col gap-4'>
            {overview.empty ? (
              <Typography variant='body2' color='text.secondary'>
                No usable wearable data for {group.label} in this window.
              </Typography>
            ) : (
              <>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  {overview.metrics.map((metric) => (
                    <KpiCard key={metric.key} metric={metric} onOpenBreakdown={() => {}} />
                  ))}
                </div>
                <FatigueTrendChart points={overview.trend.filter((point) => !point.empty)} height={200} />
              </>
            )}
          </div>
        );
      }}
    </ComparisonColumns>
  );
};
